import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  useCancelOpenclawThreadMessageJobMutation,
  useCreateOpenclawThreadMutation,
  useDeleteOpenclawThreadMutation,
  useGetOpenclawThreadQuery,
  useLazyGetOpenclawThreadActiveRunQuery,
  useLazyGetOpenclawThreadMessageJobStatusQuery,
  useListOpenclawThreadsQuery,
  useOpenclawPingQuery,
  useStartOpenclawThreadMessageJobMutation,
} from '../../store/analyticsApi';
import type { OpenClawChatMessage } from '../../types/api';
import './CommandPalette.css';

export interface OpenClawPageContext {
  intent?: 'page' | 'selection' | 'error';
  url: string;
  path: string;
  title: string;
  selectedText?: string;
  headings?: string[];
  visibleErrors?: string[];
  recentErrors?: string[];
  actions?: string[];
  pageText?: string;
  generatedAt: string;
}

interface CommandPaletteProps {
  onClose: () => void;
  initialContext?: OpenClawPageContext | null;
  currentUser?: any | null;
}

/* ── Tool event labels ── */
const TOOL_LABELS: Record<string, string> = {
  bash: 'Выполняю команду',
  read: 'Читаю файл',
  grep: 'Ищу по коду',
  glob: 'Ищу файлы',
  webfetch: 'Смотрю веб-страницу',
  question: 'Уточняю ввод',
  task: 'Запускаю подзадачу',
  write: 'Записываю файл',
  edit: 'Редактирую файл',
};

/* ── Smart greeting by time of day ── */
const getGreeting = (): string => {
  const h = new Date().getHours();
  if (h >= 6 && h < 12) return 'Доброе утро! Готов помочь с проектом.';
  if (h >= 12 && h < 18) return 'Добрый день! Чем могу помочь?';
  if (h >= 18 && h < 24) return 'Добрый вечер. Давайте разберёмся.';
  return 'Поздний запрос? Я на связи.';
};

/* ── Extract visible label from run events ── */
const getLatestStepLabel = (events: Array<{ type: string; payload: Record<string, unknown> }>): string | null => {
  for (let i = events.length - 1; i >= 0; i--) {
    const ev = events[i];
    if (ev.type === 'tool') {
      const tool = String(ev.payload?.tool || '');
      return TOOL_LABELS[tool] || (tool ? `Использую ${tool}` : null);
    }
    if (ev.type === 'phase') {
      const label = String(ev.payload?.label || '');
      if (label) return label;
    }
  }
  return null;
};

/* ── Copy to clipboard ── */
const copyToClipboard = async (text: string) => {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    // fallback
  }
};

const CommandPalette: React.FC<CommandPaletteProps> = ({ onClose }) => {
  const [query, setQuery] = useState('');
  const [selectedThreadId, setSelectedThreadId] = useState<string>('');
  const [draftMessages, setDraftMessages] = useState<OpenClawChatMessage[] | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isEntering, setIsEntering] = useState(true);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [isNearBottom, setIsNearBottom] = useState(true);
  const [hasNewMessage, setHasNewMessage] = useState(false);
  const [activeRun, setActiveRun] = useState<{ jobId: string; threadId: string; startedAt: number } | null>(null);
  const [activeRunEvents, setActiveRunEvents] = useState<Array<{ id: string; type: string; ts: string; payload: Record<string, unknown> }>>([]);

  const drawerRef = useRef<HTMLDivElement>(null);
  const chatScrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  /* ── API hooks ── */
  const { data: pingData } = useOpenclawPingQuery();
  const { data: threads = [], isLoading: isThreadsLoading } = useListOpenclawThreadsQuery();
  const [createThread, { isLoading: isCreateThreadLoading }] = useCreateOpenclawThreadMutation();
  const [deleteThread] = useDeleteOpenclawThreadMutation();
  const [startMessageJob] = useStartOpenclawThreadMessageJobMutation();
  const [fetchJobStatus] = useLazyGetOpenclawThreadMessageJobStatusQuery();
  const [fetchActiveRun] = useLazyGetOpenclawThreadActiveRunQuery();
  const [cancelRun, { isLoading: isCancellingRun }] = useCancelOpenclawThreadMessageJobMutation();

  const { data: selectedThread, isLoading: isThreadLoading } = useGetOpenclawThreadQuery(
    { thread_id: selectedThreadId },
    { skip: !selectedThreadId }
  );

  const currentMessages = useMemo(
    () => draftMessages ?? selectedThread?.messages ?? [],
    [draftMessages, selectedThread?.messages]
  );

  const selectedThreadMeta = useMemo(
    () => threads.find((t) => t.thread_id === selectedThreadId) || null,
    [selectedThreadId, threads]
  );

  const isHealthy = pingData?.healthy ?? false;
  const modelName = pingData?.model || '';

  /* ── Enter animation ── */
  useEffect(() => {
    requestAnimationFrame(() => setIsEntering(false));
  }, []);

  /* ── Escape to close ── */
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  /* ── Auto-select last thread ── */
  useEffect(() => {
    if (!selectedThreadId && threads.length > 0) {
      setSelectedThreadId(threads[0].thread_id);
    }
  }, [selectedThreadId, threads]);

  /* ── Sync draft from thread data ── */
  useEffect(() => {
    setDraftMessages(null);
  }, [selectedThreadId]);

  useEffect(() => {
    if (selectedThread?.messages) {
      setDraftMessages(selectedThread.messages);
    }
  }, [selectedThread]);

  /* ── Restore active run on mount ── */
  useEffect(() => {
    if (!selectedThreadId || activeRun) return;
    let cancelled = false;
    const restore = async () => {
      try {
        const run = await fetchActiveRun({ thread_id: selectedThreadId }, false).unwrap();
        if (!cancelled && run) {
          setActiveRun({ jobId: run.run_id, threadId: selectedThreadId, startedAt: Date.now() });
          setDraftMessages((prev) => {
            if ((prev ?? []).some((m) => m.role === 'ai' && !m.text)) return prev;
            return [
              ...(prev ?? selectedThread?.messages ?? []),
              { id: `local-ai-resume-${Date.now()}`, role: 'ai', text: '', ts: new Date().toISOString() },
            ];
          });
        }
      } catch { /* ignore */ }
    };
    void restore();
    return () => { cancelled = true; };
  }, [selectedThreadId, activeRun, fetchActiveRun, selectedThread?.messages]);

  /* ── Smart auto-scroll ── */
  const checkNearBottom = useCallback(() => {
    const el = chatScrollRef.current;
    if (!el) return;
    const threshold = 80;
    setIsNearBottom(el.scrollHeight - el.scrollTop - el.clientHeight < threshold);
  }, []);

  useEffect(() => {
    if (isNearBottom && chatScrollRef.current) {
      chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
    } else if (currentMessages.length > 0) {
      setHasNewMessage(true);
    }
  }, [currentMessages, isNearBottom]);

  /* ── Job polling ── */
  useEffect(() => {
    if (!activeRun) return undefined;
    let cancelled = false;

    const poll = async () => {
      while (!cancelled) {
        try {
          const status = await fetchJobStatus({ thread_id: activeRun.threadId, job_id: activeRun.jobId }, false).unwrap();
          if (cancelled) return;
          setActiveRunEvents(Array.isArray(status.events) ? status.events.slice(-6) : []);

          if (status.status === 'completed' && status.thread) {
            setDraftMessages(status.thread.messages);
            setActiveRunEvents([]);
            setActiveRun(null);
            return;
          }

          if (status.status === 'failed' || status.status === 'cancelled') {
            const errorDetail = status.error;
            let errorText = status.status === 'cancelled' ? 'Выполнение остановлено.' : 'Произошла ошибка при обращении к OpenCode.';
            if (typeof errorDetail === 'string' && errorDetail.trim()) {
              errorText = errorDetail;
            } else if (errorDetail && typeof errorDetail === 'object') {
              try { errorText = JSON.stringify(errorDetail, null, 2); } catch { errorText = String(errorDetail); }
            }
            setDraftMessages((prev) => {
              const base = (prev ?? []).filter((m) => !(m.role === 'ai' && !m.text));
              return [...base, { id: `local-system-${Date.now()}`, role: 'system', text: errorText, ts: new Date().toISOString() }];
            });
            setActiveRunEvents([]);
            setActiveRun(null);
            return;
          }
        } catch {
          if (!cancelled) {
            setDraftMessages((prev) => {
              const base = (prev ?? []).filter((m) => !(m.role === 'ai' && !m.text));
              return [...base, { id: `local-system-${Date.now()}`, role: 'system', text: 'Не удалось получить статус выполнения.', ts: new Date().toISOString() }];
            });
            setActiveRunEvents([]);
            setActiveRun(null);
          }
          return;
        }
        await new Promise((resolve) => window.setTimeout(resolve, 1500));
      }
    };
    void poll();
    return () => { cancelled = true; };
  }, [activeRun, fetchJobStatus]);

  /* ── Close dropdown on outside click ── */
  useEffect(() => {
    if (!isDropdownOpen) return;
    const handle = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [isDropdownOpen]);

  /* ── Ensure thread exists before sending ── */
  const ensureThread = async (): Promise<string> => {
    if (selectedThreadId) return selectedThreadId;
    const created = await createThread({}).unwrap();
    setSelectedThreadId(created.thread_id);
    return created.thread_id;
  };

  /* ── Send message ── */
  const submitMessage = async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || activeRun) return;

    const threadId = await ensureThread();
    const userMsg: OpenClawChatMessage = { id: `local-user-${Date.now()}`, role: 'user', text: trimmed, ts: new Date().toISOString() };
    const aiMsg: OpenClawChatMessage = { id: `local-ai-${Date.now() + 1}`, role: 'ai', text: '', ts: new Date().toISOString() };

    setDraftMessages((prev) => [...(prev ?? selectedThread?.messages ?? []), userMsg, aiMsg]);
    setQuery('');
    resetTextareaHeight();

    try {
      const started = await startMessageJob({
        thread_id: threadId,
        text: trimmed,
        timeout_seconds: 120,
        disable_browser: true,
        include_memory: true,
        dev_mode: false,
      }).unwrap();
      if (started.thread_id && started.thread_id !== threadId) {
        setSelectedThreadId(started.thread_id);
        setDraftMessages(null);
      }
      setActiveRun({ jobId: started.job_id, threadId: started.thread_id || threadId, startedAt: Date.now() });
    } catch (error: any) {
      const detail = error?.data?.detail;
      let errorText = 'Произошла ошибка при обращении к OpenCode.';
      if (typeof detail === 'string' && detail.trim()) errorText = detail;
      else if (detail && typeof detail === 'object') {
        try { errorText = JSON.stringify(detail, null, 2); } catch { errorText = String(detail); }
      }
      setDraftMessages((prev) => {
        const base = (prev ?? []).filter((m) => m.id !== aiMsg.id);
        return [...base, { id: `local-system-${Date.now()}`, role: 'system', text: errorText, ts: new Date().toISOString() }];
      });
    }
  };

  /* ── Cancel run ── */
  const handleCancelRun = async () => {
    if (!activeRun) return;
    try {
      await cancelRun({ thread_id: activeRun.threadId, job_id: activeRun.jobId }).unwrap();
      setActiveRun(null);
      setActiveRunEvents([]);
      setDraftMessages((prev) => {
        const base = (prev ?? []).filter((m) => !(m.role === 'ai' && !m.text));
        return [...base, { id: `local-system-cancel-${Date.now()}`, role: 'system', text: 'Выполнение остановлено.', ts: new Date().toISOString() }];
      });
    } catch { /* let polling handle it */ }
  };

  /* ── New thread ── */
  const handleNewThread = async () => {
    const created = await createThread({}).unwrap();
    setSelectedThreadId(created.thread_id);
    setQuery('');
    resetTextareaHeight();
    setIsDropdownOpen(false);
  };

  /* ── Delete thread ── */
  const handleDeleteThread = async () => {
    if (!selectedThreadId) return;
    if (!window.confirm('Удалить этот чат?')) return;
    await deleteThread({ thread_id: selectedThreadId }).unwrap();
    setSelectedThreadId('');
    setDraftMessages(null);
  };

  /* ── Form submit ── */
  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void submitMessage(query);
  };

  /* ── Textarea helpers ── */
  const autoresizeTextarea = (el: HTMLTextAreaElement) => {
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  };

  const resetTextareaHeight = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  /* ── Copy message ── */
  const handleCopy = (msg: OpenClawChatMessage) => {
    void copyToClipboard(msg.text);
    setCopiedId(msg.id);
    setTimeout(() => setCopiedId(null), 1500);
  };

  /* ── Scrim click ── */
  const handleScrimClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  /* ── Scroll to bottom on "new message" click ── */
  const scrollToBottom = () => {
    if (chatScrollRef.current) {
      chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
      setHasNewMessage(false);
      setIsNearBottom(true);
    }
  };

  const isActive = !!activeRun && activeRun.threadId === selectedThreadId;
  const stepLabel = isActive ? getLatestStepLabel(activeRunEvents) : null;

  return (
    <div className="oc-overlay" onMouseDown={handleScrimClick}>
      <div className={`oc-drawer${isEntering ? ' oc-drawer--entering' : ''}`} ref={drawerRef}>
        {/* Animated gradient border */}
        <div className="oc-drawer__gradient-border" />

        {/* ── Header ── */}
        <div className="oc-header">
          <div className="oc-header__left">
            <div className="oc-header__logo">OC</div>
            <div className="oc-header__info">
              <h3 className="oc-header__title">ИИ-Ассистент</h3>
              <div className="oc-header__status">
                <span className={`oc-status-dot ${isHealthy ? 'healthy' : pingData ? 'unhealthy' : 'checking'}`} />
                <span className="oc-status-text">{isHealthy ? 'Подключен' : pingData ? 'Недоступен' : 'Проверка...'}</span>
                {modelName && <span className="oc-status-model">{modelName}</span>}
              </div>
            </div>
          </div>

          <div className="oc-header__actions">
            {/* Thread switcher */}
            <div className="oc-thread-switcher" ref={dropdownRef}>
              <button
                type="button"
                className="oc-thread-switcher__trigger"
                onClick={() => setIsDropdownOpen((v) => !v)}
                disabled={isThreadsLoading}
              >
                <span className="oc-thread-switcher__label">
                  {selectedThreadMeta?.title || 'Новый чат'}
                </span>
                <i className={`fas fa-chevron-down oc-thread-switcher__arrow${isDropdownOpen ? ' open' : ''}`} />
              </button>

              {isDropdownOpen && (
                <div className="oc-thread-dropdown">
                  <button type="button" className="oc-thread-dropdown__new" onClick={() => void handleNewThread()} disabled={isCreateThreadLoading}>
                    <i className="fas fa-plus" />
                    <span>Новый чат</span>
                  </button>
                  {threads.length > 0 && (
                    <div className="oc-thread-dropdown__list">
                      {threads.slice(0, 10).map((thread) => (
                        <button
                          key={thread.thread_id}
                          type="button"
                          className={`oc-thread-dropdown__item${selectedThreadId === thread.thread_id ? ' active' : ''}`}
                          onClick={() => { setSelectedThreadId(thread.thread_id); setIsDropdownOpen(false); }}
                        >
                          <span className="oc-thread-dropdown__item-title">{thread.title}</span>
                          <span className="oc-thread-dropdown__item-date">
                            {new Date(thread.updated_at).toLocaleDateString()}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {selectedThreadId && (
              <button type="button" className="oc-header__btn" onClick={() => void handleDeleteThread()} title="Удалить чат">
                <i className="fas fa-trash" />
              </button>
            )}
            <button type="button" className="oc-header__btn" onClick={onClose} title="Закрыть">
              <i className="fas fa-xmark" />
            </button>
          </div>
        </div>

        {/* ── Messages ── */}
        <div className="oc-messages" ref={chatScrollRef} onScroll={checkNearBottom}>
          {currentMessages.length === 0 ? (
            <div className="oc-empty">
              <div className="oc-empty__icon">
                <i className="fas fa-magic-wand-sparkles" />
              </div>
              <p className="oc-empty__greeting">{getGreeting()}</p>
              <p className="oc-empty__hint">Задайте вопрос о проекте или попросите выполнить задачу.</p>
            </div>
          ) : (
            <div className="oc-messages__list">
              {currentMessages.map((msg) => (
                <div key={msg.id} className={`oc-msg oc-msg--${msg.role}`}>
                  {msg.role === 'ai' && !msg.text ? (
                    <div className="oc-msg__typing">
                      {stepLabel ? (
                        <span className="oc-msg__step">
                          <span className="oc-step-spinner" />
                          {stepLabel}
                        </span>
                      ) : (
                        <>
                          <span className="oc-dot" /><span className="oc-dot" /><span className="oc-dot" />
                        </>
                      )}
                    </div>
                  ) : (
                    <>
                      <div className="oc-msg__content">
                        {msg.role === 'user' ? (
                          <p>{msg.text}</p>
                        ) : (
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {msg.text}
                          </ReactMarkdown>
                        )}
                      </div>
                      {msg.role === 'ai' && msg.text && (
                        <button
                          type="button"
                          className={`oc-msg__copy${copiedId === msg.id ? ' copied' : ''}`}
                          onClick={() => handleCopy(msg)}
                          title="Копировать"
                        >
                          <i className={copiedId === msg.id ? 'fas fa-check' : 'fas fa-copy'} />
                        </button>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Scroll-to-bottom indicator */}
          {hasNewMessage && !isNearBottom && (
            <button type="button" className="oc-scroll-btn" onClick={scrollToBottom}>
              <i className="fas fa-arrow-down" />
              Новое сообщение
            </button>
          )}
        </div>

        {/* ── Active run status ── */}
        {isActive && !stepLabel && (
          <div className="oc-run-status">
            <span className="oc-step-spinner" />
            Ассистент работает. Ответ появится здесь.
          </div>
        )}

        {/* ── Input ── */}
        <form onSubmit={handleFormSubmit} className="oc-input-area">
          <div className="oc-input-wrapper">
            <textarea
              ref={textareaRef}
              className="oc-input"
              placeholder="Спросите что-нибудь..."
              value={query}
              onChange={(e) => { setQuery(e.target.value); autoresizeTextarea(e.target); }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  void submitMessage(query);
                }
              }}
              rows={1}
              disabled={isActive}
            />
            <div className="oc-input__actions">
              {isActive ? (
                <button type="button" className="oc-input__stop" onClick={() => void handleCancelRun()} disabled={isCancellingRun} title="Остановить">
                  <i className="fas fa-stop" />
                </button>
              ) : (
                <button type="submit" className="oc-input__send" disabled={!query.trim()}>
                  <i className="fas fa-paper-plane" />
                </button>
              )}
            </div>
          </div>
          <div className="oc-input-hint">
            Enter — отправить · Shift+Enter — новая строка · Esc — закрыть
          </div>
        </form>
      </div>
    </div>
  );
};

export default CommandPalette;
